// Phase TPI-C4 — Convergence Signal (pure).
//
// TPI_Logic §5. The strongest signal in the TPI stack: when 2+
// tipsters from the user's active weekend pool INDEPENDENTLY select
// the same leg, the agreement itself is informational (the pre-market
// equivalent of steam).
//
// N-TPI-03 — convergence requires FIXTURE RESOLUTION, not string
// matching. Every leg fed here must already carry a resolved
// fixture_id (from SE-9.2). Legs without one are excluded entirely:
// "Man Utd" vs "Manchester United" only converge once both resolved
// to the same fixture_id + canonical market/selection.
//
// N-TPI-04 — a Dominant convergence leg is NEVER proposed for
// removal by the system (the user may remove it manually). Strong+
// legs are protected in pass 1 unless the extractor's own substrate
// raises a veto.

import { isConvergenceEligible, type StakeSignal } from "./stakeSignal.ts";
import type { ScoredLeg, SignalDriver } from "./legScorer.ts";

export type ConvergenceLevel =
  | "none"
  | "partial" //   2 tipsters, one full one edited      +0.6
  | "confirmed" // 2 tipsters, both full                +1.2
  | "moderate" //  3 tipsters, mixed stakes             +1.4
  | "strong" //    3 tipsters, all full                 +2.5
  | "dominant"; // 4–5 tipsters, majority full          +4.0

export interface PoolLegInput {
  tipsterId: string;
  /** MUST be a resolved fixture_id (SE-9.2). Missing ⇒ excluded. */
  fixtureId: string | null | undefined;
  /** Canonical market code (SE-9.3 canonicalizeLegMarket). */
  market: string | null | undefined;
  /** Canonical selection. */
  selection: string | null | undefined;
  stake: StakeSignal;
}

export interface ConvergenceSignal {
  /** fixture_id::market::selection — the resolved identity key. */
  key: string;
  fixtureId: string;
  market: string;
  selection: string;
  agreeingTipsterIds: string[];
  fullStakeCount: number;
  level: ConvergenceLevel;
  label: string;
  uplift: number;
}

export const CONVERGENCE_UPLIFT: Record<ConvergenceLevel, number> = {
  none: 0,
  partial: 0.6,
  confirmed: 1.2,
  moderate: 1.4,
  strong: 2.5,
  dominant: 4.0,
};

const LEVEL_LABEL: Record<ConvergenceLevel, string> = {
  none: "",
  partial: "Convergence — Partial (2-tipster partial agreement)",
  confirmed: "Convergence — Confirmed (2-tipster agreement)",
  moderate: "Convergence — Moderate (3-tipster mixed)",
  strong: "Convergence — Strong (3-tipster agreement)",
  dominant: "Convergence — Dominant (pool consensus)",
};

/** §5.2 — classify a converged leg from its agreeing count + stake
 *  mix. `n` = number of distinct agreeing tipsters; `full` = how many
 *  of them at full stake. Pure. */
export function classifyConvergence(
  n: number,
  full: number,
): ConvergenceLevel {
  if (n >= 4) {
    // Dominant requires a majority at full stake.
    return full * 2 >= n ? "dominant" : "moderate";
  }
  if (n === 3) return full === 3 ? "strong" : "moderate";
  if (n === 2) return full === 2 ? "confirmed" : "partial";
  return "none";
}

function canonKey(f: string, m: string, s: string): string {
  return `${f}::${m}::${s}`;
}

/** §5.3 — compute Convergence Signals across an ingested weekend
 *  pool. N-TPI-03: legs without a resolved fixture_id are dropped
 *  before counting; identity is fixture_id + canonical market +
 *  selection, never a name string. Edited-token / unknown stakes do
 *  not count toward convergence (§4.1 via isConvergenceEligible).
 *  One tipster is counted at most once per leg. Pure. */
export function computeConvergence(
  legs: PoolLegInput[],
): ConvergenceSignal[] {
  const groups = new Map<
    string,
    {
      fixtureId: string;
      market: string;
      selection: string;
      byTipster: Map<string, StakeSignal>;
    }
  >();

  for (const leg of legs) {
    const f = leg.fixtureId;
    const m = leg.market;
    const s = leg.selection;
    // N-TPI-03: unresolved ⇒ never converges.
    if (!f || !m || !s) continue;
    // §4.1: speculative / unknown stakes don't convene.
    if (!isConvergenceEligible(leg.stake)) continue;
    const key = canonKey(f, m, s);
    let g = groups.get(key);
    if (!g) {
      g = { fixtureId: f, market: m, selection: s, byTipster: new Map() };
      groups.set(key, g);
    }
    // Highest stake a given tipster expressed on this leg wins
    // (a tipster appearing twice doesn't double-count).
    const prev = g.byTipster.get(leg.tipsterId);
    if (prev === undefined || (prev !== "full" && leg.stake === "full")) {
      g.byTipster.set(leg.tipsterId, leg.stake);
    }
  }

  const out: ConvergenceSignal[] = [];
  for (const [key, g] of groups) {
    const tipsters = [...g.byTipster.keys()];
    const n = tipsters.length;
    if (n < 2) continue;
    const full = [...g.byTipster.values()].filter((v) => v === "full").length;
    const level = classifyConvergence(n, full);
    if (level === "none") continue;
    out.push({
      key,
      fixtureId: g.fixtureId,
      market: g.market,
      selection: g.selection,
      agreeingTipsterIds: tipsters.sort(),
      fullStakeCount: full,
      level,
      label: LEVEL_LABEL[level],
      uplift: CONVERGENCE_UPLIFT[level],
    });
  }
  // Strongest first (purely informational ordering).
  return out.sort((a, b) => b.uplift - a.uplift);
}

/** §5.4 — strong/strong-er convergence is protected from pass-1
 *  removal unless the extractor's own substrate raises a veto. */
export function isStrongOrHigher(level: ConvergenceLevel): boolean {
  return level === "strong" || level === "dominant";
}

/** §5.4 / N-TPI-04 — a Dominant convergence leg is NEVER proposed
 *  for removal by the system under any circumstances. */
export function isRemovalForbidden(level: ConvergenceLevel): boolean {
  return level === "dominant";
}

/** §5.4 — removal-protection verdict for a leg. `hasExtractorVeto`
 *  is true when the extractor's OWN substrate (DNA veto / injury /
 *  Fair-Price disqualification) flags the leg — N-TPI-05: that
 *  conflict is always surfaced, never silently resolved. */
export function convergenceRemovalProtection(
  level: ConvergenceLevel,
  hasExtractorVeto: boolean,
): {
  neverPropose: boolean;
  protectedPass1: boolean;
  requiresConfirmation: boolean;
  conflict: boolean;
} {
  const dominant = isRemovalForbidden(level);
  const strong = isStrongOrHigher(level);
  return {
    // N-TPI-04: dominant is never proposed for removal, full stop.
    neverPropose: dominant,
    // Strong+ protected in pass 1 UNLESS the extractor vetoes it.
    protectedPass1: strong && !hasExtractorVeto,
    // Proposing a converged leg for removal needs explicit user
    // confirmation (PlanCard conflict notice).
    requiresConfirmation: strong,
    // Tipster conviction vs substrate signal — surfaced, never hidden.
    conflict: strong && hasExtractorVeto,
  };
}

/** N-TPI-04 enforcement at the executor boundary. A leg carrying a
 *  Dominant convergence level (leg.convergence_level === 'dominant')
 *  must NEVER end up in the dropped set — the system does not propose
 *  its removal. Any such leg found in `dropped` is pulled back into
 *  `kept` with an explicit conflict driver. Pure; returns new arrays.
 *  The user can still remove it manually upstream — this only blocks
 *  the SYSTEM from doing so. */
export function enforceDominantConvergenceProtection(
  kept: ScoredLeg[],
  dropped: { leg: ScoredLeg; source_tool: string; reason: string }[],
): {
  kept: ScoredLeg[];
  dropped: { leg: ScoredLeg; source_tool: string; reason: string }[];
} {
  if (!Array.isArray(dropped) || dropped.length === 0) {
    return { kept, dropped };
  }
  const rescued: ScoredLeg[] = [];
  const stillDropped: typeof dropped = [];
  for (const d of dropped) {
    const lvl = (d.leg as any)?.convergence_level as
      | ConvergenceLevel
      | undefined;
    if (lvl && isRemovalForbidden(lvl)) {
      const note: SignalDriver = {
        source: "convergence_signal",
        contribution_pp: 0,
        evidence:
          `Pool-consensus (Dominant) convergence — the system will ` +
          `not remove this leg (N-TPI-04). It was kept despite the ` +
          `"${d.reason}" rule. You may still remove it yourself.`,
        weight: 0,
      };
      rescued.push({
        ...d.leg,
        drivers: [...(d.leg.drivers ?? []), note],
      });
    } else {
      stillDropped.push(d);
    }
  }
  return rescued.length > 0
    ? { kept: [...kept, ...rescued], dropped: stillDropped }
    : { kept, dropped };
}
